import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Network, GraduationCap, Briefcase, TrendingUp, Users } from 'lucide-react';
import { NetworkValue, StudentEntrepreneurContext } from '../../services/analyticsApi';

interface NetworkValueCardProps {
  networkValue: NetworkValue | undefined;
  studentContext: StudentEntrepreneurContext | undefined;
}

export const NetworkValueCard: React.FC<NetworkValueCardProps> = ({ networkValue, studentContext }) => {
  if (!networkValue && !studentContext) {
    return null;
  }

  const getPotentialColor = (score: number) => {
    if (score >= 0.8) return 'bg-green-100 text-green-800 border-green-200';
    if (score >= 0.6) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Network className="h-5 w-5 text-indigo-600" />
          Network Value & Context
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Network Value Assessment */}
        {networkValue && (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-100">
              <div>
                <p className="text-sm text-gray-600">Connection Potential</p>
                <p className="text-2xl font-bold text-indigo-600">
                  {(networkValue.professional_alignment * 100).toFixed(0)}%
                </p>
              </div>
              <Badge className={getPotentialColor(networkValue.professional_alignment)}>
                {networkValue.connection_strength_potential || 'Unknown'}
              </Badge>
            </div>

            {/* Shared Interests */}
            {networkValue.shared_interests && networkValue.shared_interests.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Shared Interests</h4>
                <div className="flex flex-wrap gap-2">
                  {networkValue.shared_interests.map((interest, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {interest}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Collaboration Opportunities */}
            {networkValue.collaboration_opportunities && networkValue.collaboration_opportunities.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Collaboration Opportunities</h4>
                <ul className="space-y-1">
                  {networkValue.collaboration_opportunities.map((opp, idx) => (
                    <li key={idx} className="text-sm text-gray-600 flex items-start gap-1">
                      <TrendingUp className="h-3 w-3 text-indigo-600 mt-1 flex-shrink-0" />
                      <span>{opp}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Student/Entrepreneur Context */}
        {studentContext && (
          <div className="pt-4 border-t border-gray-200 space-y-3">
            <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Users className="h-4 w-4" />
              Participant Context
            </h4>

            {/* Is Student */}
            {studentContext.user_types?.includes('student') && (
              <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
                <GraduationCap className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-800">Student participant identified</span>
              </div>
            )}

            {/* Is Entrepreneur */}
            {studentContext.user_types?.includes('entrepreneur') && (
              <div className="flex items-center gap-2 p-2 bg-purple-50 rounded">
                <Briefcase className="h-4 w-4 text-purple-600" />
                <span className="text-sm text-purple-800">Entrepreneur participant identified</span>
              </div>
            )}

            {/* Relevant Industries */}
            {studentContext.industries_mentioned && studentContext.industries_mentioned.length > 0 && (
              <div>
                <p className="text-xs text-gray-600 mb-1">Relevant Industries</p>
                <div className="flex flex-wrap gap-1">
                  {studentContext.industries_mentioned.map((industry: string, idx: number) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {industry}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {/* Additional fields from context are displayed only if present in API types */}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

